import { theme } from '@shared/styles/theme';
import React from 'react';

export const styles: Record<string, React.CSSProperties> = {
  headerMobile: {
    padding: theme.spacing.lg,
    borderBottom: `1px solid ${theme.colors.text.white}`,
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    width: '100%',
    zIndex: 50,
    backgroundColor: theme.colors.overlay.wood85, // wood color at 85% opacity
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
    boxSizing: 'border-box',
  },
  headerDesktop: {
    padding: `${theme.spacing.lg} ${theme.spacing.xxl}`,
    borderBottom: `1px solid ${theme.colors.text.white}`,
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    width: '100%',
    zIndex: 50,
    backgroundColor: theme.colors.overlay.wood85, // wood color at 85% opacity
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
    boxSizing: 'border-box',
  },
  containerMobile: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    maxWidth: '430px',
    margin: '0 auto',
  },
  containerDesktop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    maxWidth: '1200px',
    margin: '0 auto',
    paddingLeft: '60px',
  },
  logoSection: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.md,
  },
  logoBox: {
    width: '52px',
    height: '52px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: `linear-gradient(135deg, ${theme.colors.accent.sky400} 0%, ${theme.colors.accent.blue} 100%)`,
    borderRadius: '14px',
    borderBottom: `3px solid ${theme.colors.accent.blue700}`,
  },
  title: {
    fontSize: '22px',
    fontWeight: '800',
    color: theme.colors.text.gray800,
    margin: 0,
    letterSpacing: '-0.3px',
  },
  rightSection: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.sm,
  },
  // Mobile-specific right section (avatar + hamburger only)
  mobileRightSection: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.sm,
  },
  settingsBox: {
    width: '44px',
    height: '44px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.colors.accent.orange,
    border: 'none',
    borderRadius: '12px',
    borderBottom: `3px solid ${theme.colors.accent.orange700}`,
    cursor: 'pointer',
  },
  pointsBox: {
    height: '44px',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.sm,
    backgroundColor: theme.colors.accent.yellow300,
    padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
    borderRadius: '12px',
    border: 'none',
    borderBottom: `3px solid ${theme.colors.accent.yellow700}`,
  },
  pointsText: {
    fontSize: theme.fontSize.md,
    fontWeight: '700',
    color: theme.colors.accent.orange700,
  },
  avatarBox: {
    width: '44px',
    height: '44px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.colors.accent.blue,
    borderRadius: '12px',
    borderBottom: `3px solid ${theme.colors.accent.blue700}`,
  },
  avatarText: {
    fontSize: theme.fontSize.md,
    fontWeight: 'bold',
    color: theme.colors.text.white,
  },
  notifButton: {
    width: '44px',
    height: '44px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.colors.accent.blue,
    border: 'none',
    borderRadius: '12px',
    borderBottom: `3px solid ${theme.colors.accent.blue700}`,
    cursor: 'pointer',
  },
  logoutButton: {
    width: '44px',
    height: '44px',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.sm,
    backgroundColor: theme.colors.accent.red500,
    color: theme.colors.text.white,
    border: 'none',
    borderBottom: `3px solid ${theme.colors.accent.red700}`,
    padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
    borderRadius: '12px',
    fontSize: theme.fontSize.md,
    fontWeight: '600',
    cursor: 'pointer',
  },
  // Hamburger button (same visual style as notifButton)
  hamburgerButton: {
    width: '44px',
    height: '44px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.colors.accent.blue,
    border: 'none',
    borderRadius: '12px',
    borderBottom: `4px solid rgba(0,0,0,0.2)`,
    cursor: 'pointer',
    minHeight: '44px',
  },
  // Transparent overlay that catches outside clicks to close drawer
  drawerOverlay: {
    position: 'fixed',
    inset: 0,
    zIndex: 48,
    background: 'transparent',
  },
  // Slide-down drawer panel below the header
  drawer: {
    position: 'fixed',
    top: '72px',
    left: 0,
    right: 0,
    maxWidth: '430px',
    margin: '0 auto',
    backgroundColor: theme.colors.overlay.wood85,
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    borderBottom: `1px solid ${theme.colors.border.light}`,
    zIndex: 49,
    display: 'flex',
    flexDirection: 'column',
    padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
    boxSizing: 'border-box',
  },
  // Each row inside the drawer
  drawerItem: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.md,
    padding: `${theme.spacing.md} ${theme.spacing.sm}`,
    minHeight: '44px',
    borderBottom: `1px solid ${theme.colors.border.light}`,
  },
  drawerItemLast: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.md,
    padding: `${theme.spacing.md} ${theme.spacing.sm}`,
    minHeight: '44px',
  },
  drawerItemButton: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.md,
    minHeight: '44px',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 0,
    width: '100%',
  },
  drawerItemLabel: {
    fontSize: theme.fontSize.md,
    fontWeight: '600',
    color: theme.colors.text.gray800,
  },
  drawerLogoutButton: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.md,
    minHeight: '44px',
    backgroundColor: theme.colors.accent.red500,
    color: theme.colors.text.white,
    border: 'none',
    borderBottom: `3px solid ${theme.colors.accent.red700}`,
    borderRadius: '12px',
    padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
    fontSize: theme.fontSize.md,
    fontWeight: '600',
    cursor: 'pointer',
    width: '100%',
    justifyContent: 'center',
    marginTop: theme.spacing.sm,
  },
};
